import { defineStore } from "pinia";
import { ref, computed } from "vue";
import axios from "@/plugins/axios";
import localService from "@/services/localService";
import { showMessage } from "@/utils/notifications";

// Set on login, cleared the first time the dashboard checks for a due review,
// so the access-review dialog opens at most once per login (not on reload).
const REVIEW_PENDING_KEY = "breton-accessReviewPending";

export const useInvestorAccessStore = defineStore("investorAccess", () => {
  // State
  const reviewInvestors = ref([]);
  const loading = ref(false);
  const submitting = ref(false);

  // Getters
  const getReviewInvestors = computed(() => reviewInvestors.value);
  const getIsLoading = computed(() => loading.value);
  const getIsSubmitting = computed(() => submitting.value);

  // Once-per-login flag
  const markReviewPending = () => {
    localService.setItem(REVIEW_PENDING_KEY, true);
  };

  const clearReviewPending = () => {
    localService.removeItem(REVIEW_PENDING_KEY);
  };

  // Returns whether a review check is pending for this login, and clears it.
  const consumeReviewPending = () => {
    const pending = localService.getItem(REVIEW_PENDING_KEY) === true;
    clearReviewPending();
    return pending;
  };

  // API Actions
  const fetchAccessReview = async () => {
    loading.value = true;
    try {
      const { data } = await axios.get("/investor/access-review");
      if (!data.success) {
        showMessage({ ...data, messageType: "error", messageTitle: "Error" });
        return [];
      }
      reviewInvestors.value = data.investors;
      return data.investors;
    } catch (error) {
      console.error("Error fetching investor access review:", error);
      return [];
    } finally {
      loading.value = false;
    }
  };

  const removePerson = async ({ investorId, personId }) => {
    submitting.value = true;
    try {
      const { data } = await axios.post(`/investor/${investorId}/remove-person`, {
        person_id: personId,
      });
      if (!data.success) {
        showMessage({ ...data, messageType: "error", messageTitle: "Error" });
        return data;
      }
      showMessage({ ...data, messageType: "success", messageTitle: "Success" });
      await fetchAccessReview();
      return data;
    } catch (error) {
      console.error("Error removing person from investor:", error);
      return { success: false, message: "Failed to remove access" };
    } finally {
      submitting.value = false;
    }
  };

  const confirmReview = async (investorIds) => {
    submitting.value = true;
    try {
      const { data } = await axios.post("/investor/access-review/confirm", {
        investor_ids: investorIds,
      });
      if (!data.success) {
        showMessage({ ...data, messageType: "error", messageTitle: "Error" });
        return data;
      }
      reviewInvestors.value = [];
      showMessage({
        message: "Thanks, the access list is confirmed for this year.",
        messageType: "success",
        messageTitle: "Success",
      });
      return data;
    } catch (error) {
      console.error("Error confirming investor access review:", error);
      return { success: false, message: "Failed to confirm review" };
    } finally {
      submitting.value = false;
    }
  };

  return {
    // State
    reviewInvestors,
    loading,
    submitting,

    // Getters
    getReviewInvestors,
    getIsLoading,
    getIsSubmitting,

    // Actions
    markReviewPending,
    clearReviewPending,
    consumeReviewPending,

    // API Actions
    fetchAccessReview,
    removePerson,
    confirmReview,
  };
});
